import React from 'react';
import Layout from '../../components/Layout';
import {
  Star,
  Check,
  Clock,
  Shield,
  Gift,
  ArrowRight,
  Users,
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const NetBet: React.FC = () => {
  const ratingData = [
    { category: 'Sécurité', value: 91 },
    { category: 'Bonus', value: 87 },
    { category: 'Sports', value: 85 },
    { category: 'Retrait', value: 89 },
    { category: 'Support', value: 83 },
  ];

  const payoutData = [
    { method: 'Carte Bancaire', time: 24, fee: 0 },
    { method: 'PayPal', time: 24, fee: 0 },
    { method: 'Skrill', time: 12, fee: 2.5 },
    { method: 'Neteller', time: 12, fee: 2.5 },
    { method: 'Virement Bancaire', time: 72, fee: 0 },
  ];

  const gameDistribution = [
    { name: 'Football', value: 48, color: '#6366f1' },
    { name: 'Tennis', value: 24, color: '#8b5cf6' },
    { name: 'Basketball', value: 19, color: '#ec4899' },
    { name: 'Autres', value: 9, color: '#f59e0b' },
  ];

  const monthlyTrend = [
    { month: 'Jan', users: 8500, revenue: 320000 },
    { month: 'Fév', users: 9200, revenue: 380000 },
    { month: 'Mar', users: 10000, revenue: 420000 },
    { month: 'Avr', users: 10800, revenue: 470000 },
    { month: 'Mai', users: 11500, revenue: 510000 },
    { month: 'Juin', users: 12200, revenue: 560000 },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Review',
    itemReviewed: {
      '@type': 'Organization',
      name: 'NetBet',
      description: 'Site de paris sportifs licencié',
    },
    author: {
      '@type': 'Organization',
      name: 'Primorixx',
    },
    reviewRating: {
      '@type': 'Rating',
      ratingValue: '4.6',
      bestRating: '5',
    },
    reviewBody:
      'NetBet offre un bonus équilibré de 150% jusqu\'à 150€ avec des retraits rapides. Excellente option pour les parieurs qui valorisent la rapidité de paiement.',
  };

  return (
    <Layout
      title="Analyse Complète - NetBet"
      description="Analyse détaillée du bookmaker NetBet: bonus de 150% jusqu'à 150€, sports, sécurité et méthodes de paiement. Note 4.6/5."
      canonicalUrl="/offres/netbet"
      jsonLd={jsonLd}
      ogImage="/images/offers/netbet.webp"
    >
      <div className="min-h-screen pt-20">
        <section className="py-20 bg-gradient-to-b from-dark-bg to-dark-surface relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img
              src="/images/offers/netbet.webp"
              alt="NetBet"
              className="w-full h-full object-cover"
              loading="eager"
            />
          </div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto">
              <div className="flex flex-col md:flex-row items-center md:items-start gap-8">
                <div className="flex-shrink-0">
                  <img
                    src="/images/offers/netbet.webp"
                    alt="NetBet"
                    className="w-48 h-48 object-contain bg-dark-card p-4 rounded-xl border border-dark-border"
                    loading="eager"
                  />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h1 className="text-5xl md:text-6xl font-bold mb-4 gradient-text">
                    NetBet - Analyse Complète
                  </h1>
                  <div className="flex items-center justify-center md:justify-start space-x-4 mb-4">
                    <div className="flex items-center space-x-1">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-6 h-6 ${
                            i < 5
                              ? 'fill-yellow-400 text-yellow-400'
                              : 'text-dark-border'
                          }`}
                        />
                      ))}
                      <span className="text-2xl font-bold text-white ml-2">
                        4.6
                      </span>
                    </div>
                  </div>
                  <p className="text-xl text-dark-muted mb-6">
                    Bonus équilibré avec retraits rapides et interface intuitive
                  </p>
                  <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                    <div className="flex items-center space-x-2 bg-dark-card px-4 py-2 rounded-lg">
                      <Gift className="w-5 h-5 text-dark-accent" />
                      <span className="text-white font-semibold">
                        150% jusqu'à 150€
                      </span>
                    </div>
                    <div className="flex items-center space-x-2 bg-dark-card px-4 py-2 rounded-lg">
                      <Clock className="w-5 h-5 text-dark-accent" />
                      <span className="text-white font-semibold">24-48h</span>
                    </div>
                    <div className="flex items-center space-x-2 bg-dark-card px-4 py-2 rounded-lg">
                      <Shield className="w-5 h-5 text-dark-accent" />
                      <span className="text-white font-semibold">
                        Licencié
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-dark-surface">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
              {[
                { icon: Gift, label: 'Bonus', value: '150% jusqu\'à 150€' },
                { icon: Clock, label: 'Retrait', value: '24-48h' },
                { icon: Users, label: 'Sports', value: '400+' },
                { icon: Shield, label: 'Licence', value: 'Vérifiée' },
              ].map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={index}
                    className="bg-dark-card border border-dark-border rounded-xl p-6 text-center"
                  >
                    <Icon className="w-8 h-8 text-dark-accent mx-auto mb-3" />
                    <div className="text-2xl font-bold text-white mb-1">
                      {feature.value}
                    </div>
                    <div className="text-sm text-dark-muted">{feature.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-20 bg-dark-bg">
          <div className="container mx-auto px-4">
            <h2 className="text-4xl font-bold mb-12 gradient-text text-center">
              Analyse Détaillée par Catégorie
            </h2>
            <div className="max-w-4xl mx-auto">
              <div className="bg-dark-card border border-dark-border rounded-xl p-8">
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={ratingData} layout="vertical">
                    <XAxis type="number" domain={[0, 100]} stroke="#a0a0a0" />
                    <YAxis
                      dataKey="category"
                      type="category"
                      stroke="#a0a0a0"
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1a1a2e',
                        border: '1px solid #2a2a3e',
                        borderRadius: '8px',
                      }}
                    />
                    <Bar dataKey="value" radius={[0, 8, 8, 0]}>
                      {ratingData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={
                            ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981'][
                              index
                            ]
                          }
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-dark-surface">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <div className="bg-dark-card border border-dark-border rounded-xl p-8">
                <h3 className="text-2xl font-bold text-white mb-6">
                  Répartition des Sports
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={gameDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) =>
                        `${name} ${((percent || 0) * 100).toFixed(0)}%`
                      }
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {gameDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1a1a2e',
                        border: '1px solid #2a2a3e',
                        borderRadius: '8px',
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-dark-card border border-dark-border rounded-xl p-8">
                <h3 className="text-2xl font-bold text-white mb-6">
                  Croissance Mensuelle
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={monthlyTrend}>
                    <XAxis dataKey="month" stroke="#a0a0a0" />
                    <YAxis stroke="#a0a0a0" yAxisId="left" />
                    <YAxis
                      stroke="#a0a0a0"
                      yAxisId="right"
                      orientation="right"
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1a1a2e',
                        border: '1px solid #2a2a3e',
                        borderRadius: '8px',
                      }}
                    />
                    <Legend />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="users"
                      stroke="#6366f1"
                      strokeWidth={2}
                      name="Utilisateurs"
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="revenue"
                      stroke="#8b5cf6"
                      strokeWidth={2}
                      name="Revenus (€)"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-dark-bg">
          <div className="container mx-auto px-4">
            <h2 className="text-4xl font-bold mb-12 gradient-text text-center">
              Méthodes de Paiement
            </h2>
            <div className="max-w-4xl mx-auto">
              <div className="bg-dark-card border border-dark-border rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead className="bg-dark-surface">
                    <tr>
                      <th className="px-6 py-4 text-left text-white font-semibold">
                        Méthode
                      </th>
                      <th className="px-6 py-4 text-left text-white font-semibold">
                        Délai
                      </th>
                      <th className="px-6 py-4 text-left text-white font-semibold">
                        Frais
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {payoutData.map((method, index) => (
                      <tr
                        key={index}
                        className="border-t border-dark-border hover:bg-dark-surface/50 transition-colors"
                      >
                        <td className="px-6 py-4 text-white font-medium">
                          {method.method}
                        </td>
                        <td className="px-6 py-4 text-dark-muted">
                          {method.time}h
                        </td>
                        <td className="px-6 py-4 text-dark-muted">
                          {method.fee > 0 ? `€${method.fee}` : 'Gratuit'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-dark-surface">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <div className="bg-dark-card border border-dark-border rounded-xl p-8">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
                  <Check className="w-6 h-6 text-green-400 mr-2" />
                  Avantages
                </h3>
                <ul className="space-y-3">
                  {[
                    'Bonus équilibré de 150% jusqu\'à 150€',
                    'Retraits rapides (24-48 heures)',
                    'Interface intuitive et facile à utiliser',
                    'Dépôt minimum bas (€10)',
                    'Licence vérifiée',
                    'Variété raisonnable de sports',
                  ].map((pro, index) => (
                    <li
                      key={index}
                      className="flex items-start text-dark-muted"
                    >
                      <Check className="w-5 h-5 text-green-400 mr-3 flex-shrink-0 mt-0.5" />
                      {pro}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-dark-card border border-dark-border rounded-xl p-8">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
                  <span className="w-6 h-6 text-red-400 mr-2 text-2xl">✕</span>
                  Points à Considérer
                </h3>
                <ul className="space-y-3">
                  {[
                    'Conditions de mise plus élevées (40x)',
                    'Moins de sports disponibles que les concurrents',
                    'Quelques frais sur les méthodes électroniques',
                  ].map((con, index) => (
                    <li
                      key={index}
                      className="flex items-start text-dark-muted"
                    >
                      <span className="w-5 h-5 text-red-400 mr-3 flex-shrink-0 mt-0.5 text-xl">
                        •
                      </span>
                      {con}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-dark-bg">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center bg-dark-card border border-dark-border rounded-xl p-8">
              <h2 className="text-3xl font-bold mb-4 gradient-text">
                Prêt à Commencer?
              </h2>
              <p className="text-dark-muted mb-6">
                Profitez du bonus de 150% jusqu'à 150€ et commencez à parier dès aujourd'hui!
              </p>
              <a
                href="#"
                className="inline-flex items-center px-8 py-4 bg-dark-accent hover:bg-dark-accentHover text-white rounded-lg font-semibold transition-colors"
              >
                Réclamer le Bonus
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
              <p className="text-xs text-dark-muted mt-4">
                18+ | Jouez de manière responsable | Conditions générales s'appliquent
              </p>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default NetBet;

